"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { syncPendingSessions } from "./sync";
import { trackOfflineSyncCompleted } from "@/lib/analytics";

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  const updatePendingCount = useCallback(async () => {
    try {
      const { getPendingSessionCount } = await import("./db");
      const count = await getPendingSessionCount();
      setPendingCount(count);
    } catch {
      // IndexedDB not available
    }
  }, []);

  const handleSync = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      const result = await syncPendingSessions();
      if (result.synced > 0) {
        trackOfflineSyncCompleted(result.synced, result.reviewsSynced);
        await updatePendingCount();
      }
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [updatePendingCount]);

  useEffect(() => {
    setIsOnline(navigator.onLine);

    const goOnline = () => {
      setIsOnline(true);
      handleSync();
    };
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    updatePendingCount();

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [handleSync, updatePendingCount]);

  return { isOnline, pendingCount, syncing, triggerSync: handleSync, updatePendingCount };
}
